import { ChevronDown, ChevronRight, HelpCircle } from "lucide-react";
import { useMemo, useState } from "react";
import type { BuyLogRowPatch } from "@/components/buy-log/BuyLogRow";
import { BuyLogRow } from "@/components/buy-log/BuyLogRow";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { BudgetLineItem } from "@/hooks/use-budgets";
import type { Company } from "@/hooks/use-companies";
import { formatCurrency } from "@/lib/formatters";

interface BuyLogHierarchyProps {
  lineItems: BudgetLineItem[];
  companies: Company[];
  canEdit: boolean;
  onPatchLine: (lineId: string, patch: BuyLogRowPatch) => void;
  onDeleteLine: (lineId: string) => void;
  onCreateCommitment: (line: BudgetLineItem) => void;
  onViewHistory?: (line: BudgetLineItem) => void;
  onViewCommitment?: (commitmentId: string) => void;
  recentlyCommittedLineId?: string | null;
}

interface DivisionGroup {
  divisionKey: string;
  divisionLabel: string;
  lineItems: BudgetLineItem[];
  scheduledValue: number;
  totalAward: number;
  variance: number;
}

/**
 * Group the line items by CSI division, using the cost code's csi_division
 * with a fallback to the line item's stored csiDivision field, or an
 * "Uncategorized" bucket for lines without any division info.
 */
function toCents(value: string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

function groupByDivision(lineItems: BudgetLineItem[]): DivisionGroup[] {
  const buckets = new Map<string, BudgetLineItem[]>();
  for (const item of lineItems) {
    const key = item.costCode?.csiDivision ?? item.csiDivision ?? "—";
    const list = buckets.get(key);
    if (list) list.push(item);
    else buckets.set(key, [item]);
  }

  const groups: DivisionGroup[] = [];
  for (const [key, items] of buckets) {
    let scheduledCents = 0;
    let totalAwardCents = 0;
    for (const i of items) {
      // Text-value lines carry $0 in the numeric rollup but still display
      // in the hierarchy — their intent-only scheduled value doesn't alter
      // the division subtotal (AC#9 + AC#7).
      scheduledCents += i.scheduledValueText ? 0 : toCents(i.originalBudgetedAmt);
      totalAwardCents += toCents(i.awardValue) + toCents(i.allowance) + toCents(i.holdsAtBuy);
    }
    groups.push({
      divisionKey: key,
      divisionLabel: key === "—" ? "Uncategorized" : `Division ${key}`,
      lineItems: items,
      scheduledValue: scheduledCents / 100,
      totalAward: totalAwardCents / 100,
      variance: (scheduledCents - totalAwardCents) / 100,
    });
  }
  groups.sort((a, b) => a.divisionKey.localeCompare(b.divisionKey));
  return groups;
}

/**
 * Default view described in spec §7.1 — groups line items by CSI Division
 * with a collapsible summary row per division.
 */
export function BuyLogHierarchy({
  lineItems,
  companies,
  canEdit,
  onPatchLine,
  onDeleteLine,
  onCreateCommitment,
  onViewHistory,
  onViewCommitment,
  recentlyCommittedLineId,
}: BuyLogHierarchyProps) {
  const groups = useMemo(() => groupByDivision(lineItems), [lineItems]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  function toggle(divisionKey: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(divisionKey)) next.delete(divisionKey);
      else next.add(divisionKey);
      return next;
    });
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
      <Table>
        <TableHeader className="bg-gray-50">
          <TableRow>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Cost Code
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Description
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
              Scheduled Value
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
              Award
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
              <div className="inline-flex w-full items-center justify-end gap-1">
                Allowance
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="inline-flex cursor-help">
                      <HelpCircle className="h-3 w-3 text-gray-400 hover:text-gray-600" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      Allowance amount — a lump-sum reserve for undefined scope, separate from the
                      base award value.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
              Holds
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
              Total Award
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
              Variance
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Award Status
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-center">
              <div className="inline-flex w-full items-center justify-center gap-1">
                ALW
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="inline-flex cursor-help">
                      <HelpCircle className="h-3 w-3 text-gray-400 hover:text-gray-600" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      Marks this line as an AIA allowance. Flows through to the G703 schedule of
                      values as an allowance line.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Subcontractor
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Commitment
            </TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((group) => {
            const isCollapsed = collapsed.has(group.divisionKey);
            const varianceClass =
              group.variance > 0
                ? "text-emerald-700"
                : group.variance < 0
                  ? "text-red-600"
                  : "text-gray-500";
            return [
              <TableRow
                key={`header-${group.divisionKey}`}
                className="bg-gray-50/80 hover:bg-gray-100 cursor-pointer"
                onClick={() => toggle(group.divisionKey)}
              >
                <TableCell colSpan={2} className="font-semibold text-sm text-gray-900">
                  <div className="flex items-center gap-2">
                    {isCollapsed ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    {group.divisionLabel}
                    <span className="text-xs font-normal text-gray-500">
                      ({group.lineItems.length} line{group.lineItems.length === 1 ? "" : "s"})
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-sm font-semibold text-gray-900 text-right">
                  {formatCurrency(group.scheduledValue)}
                </TableCell>
                <TableCell colSpan={3} />
                <TableCell className="text-sm font-semibold text-gray-900 text-right">
                  {formatCurrency(group.totalAward)}
                </TableCell>
                <TableCell className={`text-sm font-semibold text-right ${varianceClass}`}>
                  {formatCurrency(group.variance)}
                </TableCell>
                <TableCell colSpan={5} />
              </TableRow>,
              ...(isCollapsed
                ? []
                : group.lineItems.map((item) => (
                    <BuyLogRow
                      key={item.id}
                      lineItem={item}
                      companies={companies}
                      canEdit={canEdit}
                      onPatch={(patch) => onPatchLine(item.id, patch)}
                      onDelete={() => onDeleteLine(item.id)}
                      onCreateCommitment={() => onCreateCommitment(item)}
                      onViewHistory={onViewHistory ? () => onViewHistory(item) : undefined}
                      onViewCommitment={onViewCommitment}
                      recentlyCommitted={recentlyCommittedLineId === item.id}
                    />
                  ))),
            ];
          })}
        </TableBody>
      </Table>
    </div>
  );
}
